import React, { useState } from "react";
import {
  practiceRoomPosts,
  getAllTags,
  getPostsByTag,
  getPostById,
  DEFAULT_POST_IMAGE,
} from "../data/practiceRoomPosts";
import flowFrameLogo from "../data/FlowFrame Logo.png";

// Updated Simple markdown renderer component
const SimpleMarkdownRenderer = ({ content }) => {
  const renderContent = (text) => {
    if (!text) return null;

    // Split content into lines for processing
    const lines = text.split('\n');
    const elements = [];
    let currentParagraph = [];
    let inCodeBlock = false;
    let codeBlockContent = [];
    let listItems = [];
    let inList = false;

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const paragraphText = currentParagraph.join(' ').trim();
        if (paragraphText) {
          elements.push(
            <p key={elements.length} className="mb-4 leading-relaxed">
              {renderInlineFormatting(paragraphText)}
            </p>
          );
        }
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={elements.length} className="mb-4 space-y-2 ml-6">
            {listItems.map((item, idx) => (
              <li key={idx} className="list-disc">
                {renderInlineFormatting(item)}
              </li>
            ))}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Handle code blocks
      if (trimmedLine.startsWith('```')) {
        if (inCodeBlock) {
          // End code block
          elements.push(
            <pre key={elements.length} className="mb-4 p-4 bg-gray-100 rounded-lg overflow-x-auto">
              <code className="text-sm">{codeBlockContent.join('\n')}</code>
            </pre>
          );
          codeBlockContent = [];
          inCodeBlock = false;
        } else {
          // Start code block
          flushParagraph();
          flushList();
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeBlockContent.push(line);
        return;
      }

      // Handle headers
      if (trimmedLine.startsWith('###')) {
        flushParagraph();
        flushList();
        elements.push(
          <h3 key={elements.length} className="text-xl font-bold mb-3 mt-6">
            {renderInlineFormatting(trimmedLine.replace(/^###\s*/, ''))}
          </h3>
        );
        return;
      }

      if (trimmedLine.startsWith('##')) {
        flushParagraph();
        flushList();
        elements.push(
          <h2 key={elements.length} className="text-2xl font-bold mb-4 mt-8">
            {renderInlineFormatting(trimmedLine.replace(/^##\s*/, ''))}
          </h2>
        );
        return;
      }

      if (trimmedLine.startsWith('#')) {
        flushParagraph();
        flushList();
        elements.push(
          <h1 key={elements.length} className="text-3xl font-bold mb-5 mt-10">
            {renderInlineFormatting(trimmedLine.replace(/^#\s*/, ''))}
          </h1>
        );
        return;
      }

      // Handle horizontal rules
      if (trimmedLine === '---') {
        flushParagraph();
        flushList();
        elements.push(
          <hr key={elements.length} className="my-8 border-gray-300" />
        );
        return;
      }

      // Handle bullet points (including special emoji bullets)
      if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ') || 
          /^[✅🔹🎵]\s/.test(trimmedLine)) {
        flushParagraph();
        if (!inList) {
          inList = true;
        }
        // Handle emoji bullets specially
        if (/^[✅🔹🎵]\s/.test(trimmedLine)) {
          listItems.push(trimmedLine);
        } else {
          listItems.push(trimmedLine.replace(/^[-*]\s*/, ''));
        }
        return;
      }

      // Handle numbered lists
      if (/^\d+\.\s/.test(trimmedLine)) {
        flushParagraph();
        flushList(); // Flush any previous unordered list
        const content = trimmedLine.replace(/^\d+\.\s*/, '');
        elements.push(
          <div key={elements.length} className="mb-2 flex">
            <span className="font-medium mr-2">{trimmedLine.match(/^\d+/)[0]}.</span>
            <span>{renderInlineFormatting(content)}</span>
          </div>
        );
        return;
      }

      // Handle empty lines or lines with just spaces
      if (trimmedLine === '' || /^\s*$/.test(line)) {
        flushParagraph();
        flushList();
        return;
      }

      // Regular paragraph content
      if (inList) {
        flushList();
      }
      currentParagraph.push(line);
    });

    // Flush any remaining content
    flushParagraph();
    flushList();

    return elements;
  };

  const renderInlineFormatting = (text) => {
    if (!text) return text;

    // Handle bold text (double asterisks) - do this first
    text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Handle italic text (single asterisks) - but avoid already processed bold text
    text = text.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
    
    // Handle links
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">$1</a>');

    // Handle inline code (single backticks)
    text = text.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-gray-100 rounded text-sm font-mono">$1</code>');

    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  };

  return (
    <div className="prose prose-lg max-w-none">
      <style jsx>{`
        .prose ul li {
          margin-bottom: 0.5rem;
        }
        .prose ul li:first-child {
          margin-top: 0;
        }
      `}</style>
      {renderContent(content)}
    </div>
  );
};

export function PracticeRoomTab() {
  const [selectedPost, setSelectedPost] = React.useState(null);
  const [selectedTag, setSelectedTag] = React.useState(null);
  const [filtersCollapsed, setFiltersCollapsed] = React.useState(true);

  // Get all available tags and filtered posts
  const allTags = getAllTags();
  const filteredPosts = getPostsByTag(selectedTag);

  // Handle tag selection
  const handleTagSelect = (tag) => {
    setSelectedTag(tag === selectedTag ? null : tag);
  };

  // Handle post selection for viewing
  const handlePostSelect = (postId) => {
    const post = getPostById(postId);
    setSelectedPost(post);
  };

  // Go back to post list
  const handleBackToList = () => {
    setSelectedPost(null);
  };

  // Toggle filters visibility
  const toggleFilters = () => {
    setFiltersCollapsed(!filtersCollapsed);
  };

  // If a post is selected, show the full post view
  if (selectedPost) {
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <button
          onClick={handleBackToList}
          className="mb-4 flex items-center text-blue-600 hover:text-blue-800"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to all posts
        </button>

        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <img
            src={selectedPost.featuredImage || DEFAULT_POST_IMAGE}
            alt={selectedPost.title}
            className="w-full h-64 object-cover"
          />

          <div className="p-6">
            <h1 className="text-2xl font-bold mb-2">{selectedPost.title}</h1>
            <p className="text-gray-600 mb-4">By {selectedPost.author}</p>

            <div className="mb-4 flex flex-wrap gap-2">
              {selectedPost.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Use the simple markdown renderer */}
            <SimpleMarkdownRenderer content={selectedPost.content} />
          </div>
        </div>
      </div>
    );
  }

  // Otherwise show the tile layout with filters
  return (
    <div className="p-4">
      <div className="text-center mb-3">
        <img
          src={flowFrameLogo}
          alt="FlowFrame Logo"
          className="inline-block w-48"
        />
      </div>
      <h2 className="text-2 mb-4 text-center">Empowering Confident Artistry</h2>
      <h1 className="text-2xl font-bold mb-4">From the Practice Room</h1>

      {/* Tag filters with collapse toggle */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-medium">Filter by topic:</h3>
          <button
            onClick={toggleFilters}
            className="text-blue-600 hover:text-blue-800 focus:outline-none"
          >
            {filtersCollapsed ? "Show filters" : "Hide filters"}
          </button>
        </div>

        {!filtersCollapsed && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedTag(null)}
              className={`px-3 py-1 rounded-full text-sm border ${
                selectedTag === null
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              All
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => handleTagSelect(tag)}
                className={`px-3 py-1 rounded-full text-sm border ${
                  selectedTag === tag
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Post tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPosts.map((post) => (
          <div
            key={post.id}
            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => handlePostSelect(post.id)}
          >
            <div className="h-48 overflow-hidden">
              <img
                src={post.featuredImage || DEFAULT_POST_IMAGE}
                alt={post.title}
                className="w-full h-full object-cover transition-transform hover:scale-105"
              />
            </div>

            <div className="p-4">
              <h3 className="font-bold text-lg mb-2">{post.title}</h3>

              <div className="mb-2 flex flex-wrap gap-1">
                {post.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTagSelect(tag);
                    }}
                  >
                    {tag}
                  </span>
                ))}
                {post.tags.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                    +{post.tags.length - 3} more
                  </span>
                )}
              </div>

              <p className="text-sm text-gray-600">{post.excerpt}</p>
            </div>
          </div>
        ))}
      </div>

      {filteredPosts.length === 0 && (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No posts found with the selected tag.</p>
          <button
            onClick={() => setSelectedTag(null)}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            View all posts
          </button>
        </div>
      )}
    </div>
  );
}

export default PracticeRoomTab;
